import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import CompanyOnboardingService from "@/services/CompanyOnboardingService";
import type { CompanyDocumentDto, CompanyKycDraftDto } from "@/services/CompanyOnboardingService";
import { IdentitiesPath } from "@/screen/Identities/identitiesRoutes";
import { CompanyDetailsPath } from "@/screen/Companies/companiesRoutes";
import type { CompanyDetailsNavigationState } from "@/screen/Companies/companiesRoutes";
import { CompanyOnboardingStrings } from "@/screen/Companies/companyOnboarding.strings";
import { toCompanyOnboardingErrorMessages } from "@/screen/Companies/companyOnboardingErrors";
import { buildSaveCompanyKycRequest, getKycIssues, getMissingKycFields, getProofOfIncomeFileError, toCompanyKycFormData } from "@/screen/Companies/CompanyKyc/companyKycForm";
import type { CompanyKycField, CompanyKycFormData, CompanyKycTextField, CompanyKycYesNoField } from "@/screen/Companies/CompanyKyc/companyKycForm";

//  loading  → cargando el KYC de la empresa
//  error    → no hay empresa (sin id en la navegación, de otro usuario...) o no se ha podido cargar
//  editing  → borrador editable (NEW)
//  readOnly → la empresa ya se ha enviado: se enseña sin poder modificarla
//  done     → KYC completo y empresa enviada a Omnia: pendiente del bastanteo de Diaphanum
export type CompanyKycPhase = "loading" | "error" | "editing" | "readOnly" | "done";

export function useCompanyKycController()
{
    const history = useHistory();
    const location = useLocation<CompanyDetailsNavigationState | undefined>();
    const companyIdentityId = Number(location.state?.companyIdentityId ?? 0);

    const [phase, setPhase] = useState<CompanyKycPhase>("loading");
    const [loadError, setLoadError] = useState<string | null>(null);
    const [companyName, setCompanyName] = useState<string>("");
    const [taxId, setTaxId] = useState<string>("");
    const [formData, setFormData] = useState<CompanyKycFormData | null>(null);
    const [documents, setDocuments] = useState<CompanyDocumentDto[]>([]);
    const [showMissing, setShowMissing] = useState<boolean>(false);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [messages, setMessages] = useState<string[]>([]);
    const [errors, setErrors] = useState<string[]>([]);

    useEffect(() =>
    {
        if(!(companyIdentityId > 0))
        {
            setLoadError(CompanyOnboardingStrings.detailsNoCompany);
            setPhase("error");
            return;
        }

        let cancelled = false;
        CompanyOnboardingService.getCompanyKyc(companyIdentityId).then((response) =>
        {
            if(cancelled)
            {
                return;
            }
            if(!response.success || !response.data)
            {
                setLoadError(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.kycLoadError)[0]);
                setPhase("error");
                return;
            }

            const draft = response.data as CompanyKycDraftDto;
            setFormData(toCompanyKycFormData(draft));
            setDocuments((draft.documents ?? []).filter((document) => document.category === "ProofOfIncome"));
            setCompanyName(draft.businessName ?? "");
            setTaxId(draft.taxId ?? "");
            setPhase(draft.status === "NEW" ? "editing" : "readOnly");
        });

        return () =>
        {
            cancelled = true;
        };
    }, [companyIdentityId]);

    const clearFeedback = useCallback(() =>
    {
        setMessages([]);
        setErrors([]);
    }, []);

    const update = useCallback((changes: Partial<CompanyKycFormData>) =>
    {
        setFormData((prev) => (prev ? {...prev, ...changes} : prev));
        clearFeedback();
    }, [clearFeedback]);

    const handleTextChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    {
        update({[e.target.name as CompanyKycTextField]: e.target.value});
    }, [update]);

    const handleYesNoChange = useCallback((field: CompanyKycYesNoField, value: boolean) => update({[field]: value}), [update]);
    const handleRiskCountriesChange = useCallback((values: string[]) => update({riskCountriesWhich: values}), [update]);
    const handleFundsOriginChange = useCallback((values: string[]) => update({fundsOrigin: values}), [update]);
    const handleDeclarationChange = useCallback((checked: boolean) => update({dataTruthfulnessAccepted: checked}), [update]);

    const handleUploadDocuments = useCallback(async (_category: string, files: File[]) =>
    {
        clearFeedback();
        const fileErrors = files.map((file) => getProofOfIncomeFileError(file)).filter((message): message is string => message !== null);
        if(fileErrors.length > 0)
        {
            setErrors(Array.from(new Set(fileErrors)));
            return;
        }

        setIsUploading(true);
        const uploadErrors: string[] = [];
        for(const file of files)
        {
            const response = await CompanyOnboardingService.uploadCompanyDocument(companyIdentityId, "ProofOfIncome", file);
            if(response.success && response.data)
            {
                const uploaded = response.data as CompanyDocumentDto;
                setDocuments((prev) => [...prev, uploaded]);
            }
            else
            {
                uploadErrors.push(...toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.documentGenericError));
            }
        }
        setIsUploading(false);
        setErrors(Array.from(new Set(uploadErrors)));
    }, [companyIdentityId, clearFeedback]);

    const handleDeleteDocument = useCallback(async (documentId: number) =>
    {
        clearFeedback();
        const response = await CompanyOnboardingService.deleteCompanyDocument(companyIdentityId, documentId);
        if(response.success || response.errors?.some((error) => error.code === "DOCUMENT_NOT_FOUND"))
        {
            setDocuments((prev) => prev.filter((document) => document.id !== documentId));
            return;
        }
        setErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.documentGenericError));
    }, [companyIdentityId, clearFeedback]);

    const save = useCallback(async (complete: boolean) =>
    {
        if(!formData)
        {
            return;
        }

        clearFeedback();
        setIsSaving(true);
        const response = await CompanyOnboardingService.saveCompanyKyc(companyIdentityId, buildSaveCompanyKycRequest(formData, complete));

        if(response.success)
        {
            if(complete)
            {
                // KYC guardado y completo: se envía la empresa a Omnia (alta + AML). Si falla, sigue en borrador
                const submission = await CompanyOnboardingService.submitCompany(companyIdentityId);
                setIsSaving(false);
                window.scrollTo(0, 0);
                if(submission.success)
                {
                    setPhase("done");
                    return;
                }
                setErrors(toCompanyOnboardingErrorMessages(submission.errors, CompanyOnboardingStrings.kycSubmitError));
                return;
            }
            setIsSaving(false);
            setMessages([CompanyOnboardingStrings.draftSaved]);
            return;
        }
        setIsSaving(false);
        // Al enviar con datos que faltan el backend guarda igualmente el borrador y devuelve lo que falta
        setErrors(toCompanyOnboardingErrorMessages(response.errors, CompanyOnboardingStrings.kycGenericError));
        if(complete)
        {
            window.scrollTo(0, 0);
        }
    }, [formData, companyIdentityId, clearFeedback]);

    // Lo que falta para enviar a bastanteo, con el mismo criterio que el backend
    const missingFields = useMemo(() => (formData ? getMissingKycFields(formData) : []), [formData]);
    const pendingIssues = useMemo(() => (formData ? getKycIssues(formData, documents) : []), [formData, documents]);

    const handleSaveDraft = useCallback(() => save(false), [save]);

    const handleSendToReview = useCallback(async () =>
    {
        setShowMissing(true);
        if(pendingIssues.length > 0)
        {
            setMessages([]);
            setErrors([CompanyOnboardingStrings.kycIncomplete, ...pendingIssues]);
            window.scrollTo(0, 0);
            return;
        }
        await save(true);
    }, [pendingIssues, save]);

    const goToIdentities = useCallback(() =>
    {
        history.push(IdentitiesPath);
    }, [history]);

    // Vuelve al paso 3 con la misma empresa (el id viaja por el state, no por la URL)
    const goToCompanyDetails = useCallback(() =>
    {
        history.push(CompanyDetailsPath, { companyIdentityId } as CompanyDetailsNavigationState);
    }, [history, companyIdentityId]);

    return{
        phase,
        loadError,
        companyName,
        taxId,
        formData,
        documents,
        isEditable: phase === "editing",
        isMissing: (field: CompanyKycField): boolean => showMissing && missingFields.includes(field),
        isSaving,
        isUploading,
        messages,
        errors,
        canSend: phase === "editing" && formData?.dataTruthfulnessAccepted === true && !isSaving && !isUploading,
        handleTextChange,
        handleYesNoChange,
        handleRiskCountriesChange,
        handleFundsOriginChange,
        handleDeclarationChange,
        handleUploadDocuments,
        handleDeleteDocument,
        handleSaveDraft,
        handleSendToReview,
        goToIdentities,
        goToCompanyDetails,
    };
}
